import React, { FC } from 'react';
import { Button, Flex, Popover, Text, TextButton } from '@amzn/storm-ui';
import styled from 'styled-components';
import useIntl from '@i18n/useIntl';
import {
  COMBINE_ACCOUNTS_CANCEL_KEY,
  COMBINE_ACCOUNTS_GO_BACK_KEY,
  COMBINE_ACCOUNTS_SUBMIT_KEY,
} from '@features/combine-accounts/constants';

const PopoverBody = styled.div`
  display: inline-flex;
  max-width: 400px;
  padding: 20px;
  gap: 12px;
`;

const SubmitWrapper = styled.div`
  display: inline-flex;
`;

interface CombineAccountsActionButtonsProps {
  onCancel: () => void;
  onGoBack?: () => void;
  onSubmit: () => void;
  submitDisabled?: boolean;
  // Shows a spinner on the submit button while the action is in flight. Real
  // Storm additionally treats a loading button as disabled and inert, but the
  // test double in src/tests/__mocks__/storm-ui.tsx does not, so callers should
  // pass submitDisabled for the same window rather than relying on that. This
  // prop is for the spinner; submitDisabled is what actually blocks input.
  submitLoading?: boolean;
  submitLabelKey?: string;
  submitRef?: React.RefObject<HTMLDivElement>;
  popoverError?: string | null;
  onPopoverClose?: () => void;
}

const CombineAccountsActionButtons: FC<CombineAccountsActionButtonsProps> = ({
  onCancel,
  onGoBack,
  onSubmit,
  submitDisabled,
  submitLoading,
  submitLabelKey,
  submitRef,
  popoverError,
  onPopoverClose,
}) => {
  const { intl } = useIntl();
  return (
    <Flex alignItems="center" columnGap="xxsmall">
      <TextButton onClick={onCancel}>
        {intl.getMessage(COMBINE_ACCOUNTS_CANCEL_KEY)}
      </TextButton>
      {onGoBack && (
        <Button onClick={onGoBack}>
          {intl.getMessage(COMBINE_ACCOUNTS_GO_BACK_KEY)}
        </Button>
      )}
      <SubmitWrapper ref={submitRef}>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={submitDisabled}
          loading={submitLoading}
        >
          {intl.getMessage(submitLabelKey ?? COMBINE_ACCOUNTS_SUBMIT_KEY)}
        </Button>
      </SubmitWrapper>
      {popoverError && submitRef?.current && (
        <Popover
          isOpen
          variant="inverted"
          withCloseButton
          onCloseButtonClick={onPopoverClose}
          trigger={submitRef as React.RefObject<HTMLElement>}
          position="top"
          padding="none"
        >
          <PopoverBody>
            <Text>{popoverError}</Text>
          </PopoverBody>
        </Popover>
      )}
    </Flex>
  );
};

export default CombineAccountsActionButtons;
